import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/services/api';
import { DashboardLayout } from '@/components/Layout';
import { PageLoader } from '@/components/common';
import {
  LoginPage,
  DashboardPage,
  DashboardTechPage,
  OrganizationsPage,
  UsersPage,
  RolesPage,
  DatabasesPage,
  StoragePage,
  ServerPage,
  BackupsPage,
  AuditPage,
  SettingsPage,
  AcceptInvitationPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  LeadRequestPage,
  LeadLinkFormPage,
  EcommercePage,
  PetPage,
  MetadataPage,
  CatalogPage,
  WebhooksPage,
  ApiKeysPage,
  ProductsPage,
  ProjectsManagerPage,
  CustomersPage,
  CollaboratorsPage,
  BlogPage,
  MarketingPage,
  PlannerPage,
  ProcessesPage,
  BudgetPage,
  MembersPage,
  FeesPage,
  MeetingsPage,
  CrmContactsPage,
  CrmPipelinePage,
} from '@/pages';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PageLoader />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PageLoader />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { accessToken, setLoading, logout, setUser, setPlatformPermissions } = useAuthStore();

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken) {
        try {
          const response = await authApi.me();
          setUser(response.data.user);
          setPlatformPermissions(response.data.platformPermissions || []);
        } catch {
          // Token invalid, logout
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/accept-invitation"
        element={<AcceptInvitationPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Captación pública del lead (VIDA-356) */}
      <Route path="/solicitud/:orgSlug" element={<LeadRequestPage />} />
      <Route path="/lead/:orgSlug/:token" element={<LeadLinkFormPage />} />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/system/dashboard-tech" element={<DashboardTechPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/databases" element={<DatabasesPage />} />
        <Route path="/storage" element={<StoragePage />} />
        <Route path="/server" element={<ServerPage />} />
        <Route path="/backups" element={<BackupsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/ecommerce" element={<EcommercePage />} />
        <Route path="/pet" element={<PetPage />} />
        <Route path="/metadata" element={<MetadataPage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/api-keys" element={<ApiKeysPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/projects" element={<ProjectsManagerPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/colaboradores" element={<CollaboratorsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/planner" element={<PlannerPage />} />
        <Route path="/processes" element={<ProcessesPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/fees" element={<FeesPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/meetings" element={<MeetingsPage />} />
        <Route path="/crm/contacts" element={<CrmContactsPage />} />
        <Route path="/crm/pipeline" element={<CrmPipelinePage />} />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
